// Matriz papel × permissão do M0 (subconjunto do Anexo A.3) só para ocultar navegação e botões no shell.
// O servidor é a autoridade (Parte 14.1): todo controle escondido continua bloqueado na API.
// TODO(GIY-050): substituir por catálogo publicado pela API de autorização (matriz versionada, Parte 5).

export type Permission = string;

const NETWORK_ROLES: Readonly<Record<string, readonly Permission[]>> = {
  HOLDING_ADMIN: [
    'network.read',
    'platform.health.read',
    'settings.read',
    'settings.manage',
    'featureflag.read',
    'identity.user.manage',
    'identity.session.revoke',
    'identity.mfa.reset.request',
    'identity.mfa.reset.approve',
    'identity.temporary.manage',
    'identity.client.manage',
    'identity.key.manage',
    'tenant.read.network',
    'tenant.create',
    'tenant.lifecycle.approve',
    'holiday.read',
    'holiday.manage',
    'authz.read',
    'authz.assignment.read',
    'authz.role.assign',
    'authz.history.read',
    'exception.manage',
    'workflow.policy.manage',
    'audit.read.network',
    'study.network.read',
    'notification.template.test-send',
    // M1 (Anexo A.1, OPEN-058): novas permissões de tarefa/aprovação/template — proposta de concessão A.3
    // (plano de domínio M1 §6.1); a autoridade real fica no backend (`DenyAllPermissionEvaluator` até o M4).
    // `notification.template.read`/`approval.decide` aqui cobrem o aprovador de demonstração do modo fake
    // (workflowStore.seedWorkflowState, TODO GIY-025 — a política real usa QUALITY_LEAD/DPO).
    'task.read',
    'task.manage',
    'approval.decide',
    'notification.template.read',
    'notification.template.manage',
    'notification.template.approve',
    'document.read',
    'document.create',
    'document.upload',
    'document.manage',
  ],
  HOLDING_LEADERSHIP: [
    'network.read',
    'platform.health.read',
    'tenant.read.network',
    'capacity.read',
    'report.read',
    'study.network.read',
    'organization.read.network',
    'supplier.read.network',
    'procedure.read.network',
    'featureflag.read',
  ],
  HOLDING_OPERATIONS_PLANNER: [
    'tenant.read.network',
    'site.read',
    'capacity.read',
    'capacity.manage.network',
    'report.read',
    'person.read.network',
    'person.link.read',
    'exception.manage',
    'study.network.read',
    'organization.read.network',
    'organization.manage.network',
    'supplier.read.network',
    'procedure.read.network',
    'procedure.manage.network',
    'task.read',
    'task.manage',
  ],
  STARTUP_SPECIALIST: [
    'study.network.read',
    'study.network.manage',
    'organization.read.network',
    'organization.manage.network',
    'tenant.read.network',
    'site.read',
    'document.read',
    'document.create',
    'document.upload',
    'document.manage',
    'supplier.read.network',
    'procedure.read.network',
    'procedure.manage.network',
    'procedure.publish.network',
  ],
  QUALITY_LEAD: [
    'authz.read',
    'authz.matrix.edit',
    'authz.matrix.publish',
    'authz.sod.manage',
    'authz.history.read',
    'access.review.read',
    'breakglass.review',
    'quality.signals.read',
    'audit.read.network',
    'audit.export',
    'supplier.read.network',
    'supplier.qualification.read',
    'supplier.qualification.assess',
    'supplier.qualification.approve',
    'procedure.read.network',
    'procedure.publish.network',
    'notification.template.approve',
    'notification.template.read',
    'approval.decide',
    'document.read',
    'settings.manage',
    'report.read',
    'exception.manage',
    // M5 (Anexo A.3, GIY-067): leitura da Matriz de Enquadramento e verificação de certificação por segunda pessoa.
    'people.matrix.read',
    'person.credential.read',
    'person.credential.verify',
    'person.credential.revoke',
  ],
  INFOSEC_LEAD: [
    'identity.session.revoke',
    'identity.client.manage',
    'identity.key.manage',
    'authz.read',
    'authz.sod.manage',
    'authz.history.read',
    'breakglass.review',
    'audit.read.network',
    'platform.health.read',
    'settings.read',
    'featureflag.read',
  ],
  DPO: [
    'authz.matrix.publish',
    'breakglass.review',
    'audit.read.network',
    'audit.export',
    'notification.template.approve',
    'notification.template.read',
    'approval.decide',
    'document.read',
    'people.matrix.read',
    // M5 (Anexo A.3): identificador fiscal e nascimento só com o propósito `PrivacyRequest` (X-Purpose).
    'person.sensitive.read',
    'report.read',
  ],
  PRODUCT_OWNER: [
    'settings.read',
    'settings.manage',
    'featureflag.read',
    'workflow.policy.manage',
    'notification.template.manage',
    'notification.template.read',
    'notification.template.test-send',
    'platform.health.read',
    'report.read',
    'tenant.read.network',
    'study.network.read',
    'organization.read.network',
  ],
  PEOPLE_ADMIN: [
    'person.read.network',
    'person.manage.network',
    // M5 (Anexo A.3, GIY-067): classe T detida também pela rede (`NetworkGrantable`), mais os atos de duas chaves (1.ª chave).
    'person.manage',
    'person.sensitive.read',
    'person.link.read',
    'person.link.manage',
    'person.link.exception.request',
    'people.matrix.read',
    'people.matrix.manage',
    'person.credential.read',
    'person.credential.manage',
    'person.schedule.manage',
    'person.import',
    'people.history.read',
    'identity.user.manage',
    'access.review.decide',
  ],
  // 2.ª chave da Matriz e da exceção de transição (Jurídico); lê a versão que aprova (OPEN-095).
  LEGAL: [
    'people.matrix.read',
    'people.matrix.manage',
    'person.link.exception.request',
    'ownership.read',
    'ownership.manage',
    'tenant.contract.manage',
    'document.read',
    'supplier.read.network',
  ],
  SUPPLIER_MANAGER: [
    'supplier.read.network',
    'supplier.manage.network',
    'supplier.coverage.manage',
    'supplier.qualification.read',
    'supplier.qualification.assess',
    'serviceorder.read',
    'document.read',
    'document.create',
    'document.upload',
  ],
};

const TENANT_ADMIN_BASE: readonly Permission[] = [
  'tenant.read',
  'tenant.manage',
  'site.read',
  'site.manage',
  'site.calendar.manage',
  'tenant.settings.read',
  'tenant.settings.manage',
  'person.read',
  'person.manage',
  'person.link.read',
  'person.link.manage',
  'person.credential.read',
  'person.credential.manage',
  'person.schedule.manage',
  'person.import',
  'room.read',
  'room.manage',
  'equipment.read',
  'equipment.manage',
  'equipment.calibration.record',
  'supply.manage',
  'kit.read',
  'kit.manage',
  'procedure.read',
  'supplier.read',
  'supplier.create',
  'supplier.qualification.read',
  'serviceorder.read',
  'serviceorder.manage',
  'calendar.read',
  'calendar.block.manage',
  'capacity.read',
  'report.read',
  'exception.manage',
  'audit.read',
  'task.read',
  'task.manage',
  'document.read',
  'document.create',
  'document.upload',
  'document.manage',
  'document.approve',
];

const STUDY_COORDINATOR_BASE: readonly Permission[] = [
  'study.read',
  'study.config.read',
  'procedure.read',
  'participant.read',
  'participant.manage',
  'participant.identifiers.read',
  'participant.anchor.record',
  'participant.consent.record',
  'appointment.read',
  'appointment.book',
  'appointment.reschedule',
  'appointment.cancel',
  'appointment.execute',
  'appointment.notify',
  'appointment.guide.generate',
  'appointment.relations.manage',
  'appointment.exception.request',
  'calendar.read',
  'document.read',
  'document.upload',
  'supplier.read',
  'serviceorder.read',
  'serviceorder.manage',
  'kit.read',
  'audit.read',
  'exception.manage',
];

const TENANT_ROLES: Readonly<Record<string, readonly Permission[]>> = {
  TENANT_ADMIN: [
    ...TENANT_ADMIN_BASE,
    'identity.user.manage.tenant',
    'identity.temporary.manage',
    'authz.assignment.read.tenant',
    'authz.role.assign.tenant',
    'access.review.decide',
    'export.tenant.request',
  ],
  SITE_OPERATIONS_LEAD: [
    ...TENANT_ADMIN_BASE,
    'procedure.manage',
    'study.config.review.operational',
    'appointment.exception.approve',
    'study.activate',
    'equipment.calibration.verify',
  ],
  STARTUP_SPECIALIST: [
    'procedure.read',
    'study.read',
    'study.config.read',
    'study.config.edit',
    'study.readiness.manage',
    'study.import',
    'participant.read',
    'participant.manage',
    'document.read',
    'site.read',
  ],
  PRINCIPAL_INVESTIGATOR: [
    'person.read',
    'calendar.read',
    'capacity.read',
    'report.read',
    'document.read',
    'procedure.read',
    'procedure.publish',
    'supplier.read',
    'study.read',
    'study.manage',
    'study.config.read',
    'study.config.review.clinical',
    'study.config.publish',
    'study.readiness.manage',
    'study.activate',
    'delegation.read',
    'delegation.manage',
    'delegation.sign',
    'participant.read',
    'participant.identifiers.read',
    'appointment.read',
    'appointment.relations.manage',
    'appointment.exception.approve',
    'document.approve',
    'audit.read',
    'breakglass.use',
  ],
  CLINICAL_REVIEWER: [
    'study.read',
    'study.config.read',
    'study.config.review.clinical',
    'procedure.read',
    'appointment.exception.approve',
    'document.read',
  ],
  SUB_INVESTIGATOR: [
    'study.read',
    'participant.read',
    'participant.identifiers.read',
    'participant.anchor.record',
    'participant.consent.record',
    'appointment.read',
    'appointment.execute',
    'document.read',
    'breakglass.use',
  ],
  LEAD_COORDINATOR: [
    ...STUDY_COORDINATOR_BASE,
    'study.config.edit',
    'study.config.review.operational',
    'study.readiness.manage',
    'study.import',
    'appointment.exception.approve',
    'participant.merge.request',
    'delegation.manage',
  ],
  STUDY_COORDINATOR: STUDY_COORDINATOR_BASE,
  PHARMACIST_RT: [
    'study.read',
    'participant.read',
    'appointment.read',
    'appointment.execute',
    'equipment.read',
    'equipment.calibration.record',
    'kit.read',
    'kit.manage',
    'document.read',
  ],
  CLINICAL_STAFF: [
    'study.read',
    'participant.read',
    'appointment.read',
    'appointment.execute',
    'document.read',
    'calendar.read',
  ],
  DATA_ENTRY: [
    'study.read',
    'participant.read',
    'appointment.read',
    'participant.anchor.record',
    'document.read',
  ],
  SCHEDULER_RECEPTION: ['calendar.read', 'room.read', 'appointment.execute', 'site.read'],
  // M5 (Anexo A.3): qualidade local — somente leitura operacional; verifica certificações (segunda pessoa, nunca quem registrou).
  LOCAL_QUALITY: [
    'audit.read',
    'quality.signals.read',
    'supplier.read',
    'supplier.qualification.read',
    'procedure.read',
    'equipment.read',
    'person.credential.read',
    'person.credential.verify',
    'person.credential.revoke',
    'document.read',
    'report.read',
  ],
  // M6 (Anexo A.3, GIY-075): somente leitura, com expiração obrigatória; lê o delegation log do estudo (nunca assina nem prepara).
  EXTERNAL_AUDITOR: [
    'document.read',
    'audit.read',
    'delegation.read',
    'study.read',
    'appointment.read',
  ],
};

const ALL_NETWORK_PERMISSIONS: readonly Permission[] = Array.from(
  new Set(Object.values(NETWORK_ROLES).flat()),
);
const ALL_TENANT_PERMISSIONS: readonly Permission[] = Array.from(
  new Set(Object.values(TENANT_ROLES).flat()),
);
export const ALL_FULL_ADMIN_PERMISSIONS: readonly Permission[] = Array.from(
  new Set([...ALL_NETWORK_PERMISSIONS, ...ALL_TENANT_PERMISSIONS]),
);

/** Permissões de classe N de um papel de rede (vazio para papel desconhecido: negado por padrão). */
export function networkRolePermissions(role: string): readonly Permission[] {
  if (role === 'SUPER_USER' || role === 'SUPER_ADMIN') {
    return ALL_FULL_ADMIN_PERMISSIONS;
  }
  return NETWORK_ROLES[role] ?? [];
}

/** Permissões de classe T/S de um papel de tenant/site/estudo (vazio para papel desconhecido). */
export function tenantRolePermissions(role: string): readonly Permission[] {
  if (role === 'SUPER_USER' || role === 'SUPER_ADMIN') {
    return ALL_FULL_ADMIN_PERMISSIONS;
  }
  return TENANT_ROLES[role] ?? [];
}
